import { useMemo, useCallback, useState } from 'react';

import { getLocalStorage } from 'libs/common';
import { useStateStorage } from './use-state-storage';

// persisted per page: { order: [name...], hidden: [name...], widths: { name: px } }
const storage = getLocalStorage('gridColumns');

const MIN_WIDTH = 50;

/**
 * hook for user-customizable table columns: order, visibility, width.
 * settings are kept in localStorage, keyed by `page`.
 *
 * @param {string} page unique key of the grid (e.g. 'stays')
 * @param {Array} defaultFields default `fields` config, as used by the `Table` component
 * @returns {{
 *   fields: Array,        fields to pass into <Table fields={...} />, respecting saved order/visibility/width
 *   columns: Array,       full list ({ name, title, active, forced }) for the settings panel, incl. hidden ones
 *   toggleColumn: Function,
 *   reorderColumns: Function,
 *   resizeColumn: Function,
 *   resetColumns: Function,
 * }}
 */
export const useGridColumns = (page, defaultFields) => {
  const [saved, setSaved] = useStateStorage(page, null, storage);
  // width being dragged right now (not yet persisted) - kept in memory only, so the whole column
  // (header + every row cell) resizes live, without hitting localStorage on every pixel of movement
  const [liveWidths, setLiveWidths] = useState({});

  const byName = useMemo(() => {
    const out = {};
    defaultFields.forEach((field) => {
      out[field.name] = field;
    });
    return out;
  }, [defaultFields]);

  const defaultOrder = useMemo(() => defaultFields.map((field) => field.name), [defaultFields]);

  // saved order, filtered to fields that still exist, with newly added fields appended at the end
  const order = useMemo(() => {
    const savedOrder = (saved?.order || []).filter((name) => byName[name]);
    const missing = defaultOrder.filter((name) => !savedOrder.includes(name));
    return [...savedOrder, ...missing];
  }, [saved, byName, defaultOrder]);

  const hidden = saved?.hidden || [];
  const widths = saved?.widths || {};

  const persist = useCallback(
    (patch) => {
      setSaved({ order, hidden, widths, ...patch });
    },
    [order, hidden, widths, setSaved],
  );

  // full list, used by the settings panel (includes hidden columns)
  const columns = useMemo(
    () =>
      order.map((name) => {
        const field = byName[name];
        return {
          name,
          title: field.title,
          active: !field.isHidden && !hidden.includes(name),
          forced: !!field.isHidden, // hardcoded in the grid config, can't be toggled by the user
        };
      }),
    [order, byName, hidden],
  );

  const toggleColumn = useCallback(
    (name) => {
      const isActive = !hidden.includes(name);
      const nextHidden = isActive ? [...hidden, name] : hidden.filter((el) => el !== name);
      persist({ hidden: nextHidden });
    },
    [hidden, persist],
  );

  const reorderColumns = useCallback(
    (fromName, toName) => {
      if (fromName === toName) {
        return;
      }
      const next = order.slice();
      const fromIdx = next.indexOf(fromName);
      const toIdx = next.indexOf(toName);
      if (fromIdx === -1 || toIdx === -1) {
        return;
      }
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromName);
      persist({ order: next });
    },
    [order, persist],
  );

  // live preview while dragging: updates in-memory only, applied to the whole column immediately
  const previewResizeColumn = useCallback((name, newWidth) => {
    setLiveWidths((prev) => ({ ...prev, [name]: Math.max(MIN_WIDTH, Math.round(newWidth)) }));
  }, []);

  // called once the user releases the mouse: writes the final width to localStorage
  const resizeColumn = useCallback(
    (name, newWidth) => {
      const value = Math.max(MIN_WIDTH, Math.round(newWidth));
      setLiveWidths((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
      persist({ widths: { ...widths, [name]: value } });
    },
    [widths, persist],
  );

  // fields actually passed to the Table component
  const fields = useMemo(
    () =>
      order.map((name) => {
        const field = byName[name];
        const width = liveWidths[name] ?? widths[name];
        return {
          ...field,
          isHidden: field.isHidden || hidden.includes(name),
          ...(width ? { width, $resized: true } : {}),
          resizable: true,
          onResize: (newWidth) => previewResizeColumn(name, newWidth),
          onResizeEnd: (newWidth) => resizeColumn(name, newWidth),
        };
      }),
    [order, byName, hidden, widths, liveWidths, previewResizeColumn, resizeColumn],
  );

  const resetColumns = useCallback(() => {
    setSaved(null);
  }, [setSaved]);

  return { fields, columns, toggleColumn, reorderColumns, resizeColumn, resetColumns };
};
